import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { formatDistanceToNow } from 'date-fns'
import {
    FiBell,
    FiCheck,
    FiTrash2,
    FiFilter,
    FiRefreshCw,
    FiSettings,
    FiEye,
    FiUser,
    FiDollarSign,
    FiBriefcase,
    FiMessageSquare,
    FiStar,
    FiAlertCircle,
    FiGift,
    FiChevronDown,
    FiMail,
    FiX
} from 'react-icons/fi'
import { RootState, AppDispatch } from '@/store/store'
import {
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead
} from '@/store/slices/notificationsSlice'
import { NotificationType, Notification } from '@/types/notification'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import NotificationPreferences from '@/components/notifications/NotificationPreferences'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useConfirmDialog } from '@/hooks/useConfirmDialog'

const NotificationsPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const { showSuccess } = useSnackbarContext()
    const { dialogState, showConfirmDialog, closeDialog, handleConfirm } = useConfirmDialog()

    const {
        notifications,
        unreadCount,
        isLoading,
        hasMore,
        currentPage,
        totalPages
    } = useSelector((state: RootState) => state.notifications)

    const [filterType, setFilterType] = useState<NotificationType | 'all'>('all')
    const [filterRead, setFilterRead] = useState<'all' | 'read' | 'unread'>('all')
    const [showFilters, setShowFilters] = useState(false)
    const [selectedNotifications, setSelectedNotifications] = useState<string[]>([])
    const [bulkMode, setBulkMode] = useState(false)
    const [showPreferences, setShowPreferences] = useState(false)

    useEffect(() => {
        dispatch(fetchNotifications({ page: 1, limit: 20 }))
    }, [dispatch])

    const notificationTypes: { value: NotificationType | 'all'; label: string }[] = [
        { value: 'all', label: 'All Notifications' },
        { value: 'gig_application', label: 'Gig Applications' },
        { value: 'application_status', label: 'Application Status' },
        { value: 'message_received', label: 'Messages' },
        { value: 'gig_reminder', label: 'Reminders' },
        { value: 'payment_received', label: 'Payments Received' },
        { value: 'payment_sent', label: 'Payments Sent' },
        { value: 'profile_view', label: 'Profile Views' },
        { value: 'rating_received', label: 'Ratings' },
        { value: 'system_update', label: 'System Updates' },
        { value: 'account_security', label: 'Security Alerts' },
        { value: 'promotional', label: 'Promotional' },
        { value: 'admin_broadcast', label: 'Admin Announcements' },
        { value: 'admin_individual', label: 'Admin Messages' },
        { value: 'system_maintenance', label: 'System Maintenance' },
        { value: 'policy_update', label: 'Policy Updates' },
        { value: 'feature_announcement', label: 'New Features' },
        { value: 'verification_update', label: 'Document Verification' },
        { value: 'document_verified', label: 'Documents Verified' },
        { value: 'document_rejected', label: 'Documents Rejected' }
    ]

    const getNotificationIcon = (type: NotificationType) => {
        const iconMap: Record<NotificationType, JSX.Element> = {
            gig_application: <FiBriefcase className="w-5 h-5" />,
            application_status: <FiCheck className="w-5 h-5" />,
            message_received: <FiMessageSquare className="w-5 h-5" />,
            gig_reminder: <FiBell className="w-5 h-5" />,
            payment_received: <FiDollarSign className="w-5 h-5" />,
            payment_sent: <FiDollarSign className="w-5 h-5" />,
            profile_view: <FiUser className="w-5 h-5" />,
            rating_received: <FiStar className="w-5 h-5" />,
            system_update: <FiSettings className="w-5 h-5" />,
            account_security: <FiAlertCircle className="w-5 h-5" />,
            promotional: <FiGift className="w-5 h-5" />,
            welcome: <FiGift className="w-5 h-5" />,
            email_verification: <FiCheck className="w-5 h-5" />,
            login_alert: <FiAlertCircle className="w-5 h-5" />,
            password_changed: <FiSettings className="w-5 h-5" />,
            profile_updated: <FiUser className="w-5 h-5" />,
            account_deactivated: <FiAlertCircle className="w-5 h-5" />,
            gig_posted: <FiBriefcase className="w-5 h-5" />,
            gig_updated: <FiBriefcase className="w-5 h-5" />,
            gig_expired: <FiBell className="w-5 h-5" />,
            gig_deleted: <FiTrash2 className="w-5 h-5" />,
            admin_broadcast: <FiBell className="w-5 h-5" />,
            admin_individual: <FiMail className="w-5 h-5" />,
            system_maintenance: <FiSettings className="w-5 h-5" />,
            policy_update: <FiAlertCircle className="w-5 h-5" />,
            feature_announcement: <FiGift className="w-5 h-5" />,
            verification: <FiCheck className="w-5 h-5" />,
            document_verification: <FiCheck className="w-5 h-5" />,
            verification_status_changed: <FiCheck className="w-5 h-5" />,
            document_uploaded: <FiCheck className="w-5 h-5" />,
            verification_completed: <FiCheck className="w-5 h-5" />,
            document_rejected: <FiAlertCircle className="w-5 h-5" />,
            document_approved: <FiCheck className="w-5 h-5" />,
            verification_update: <FiCheck className="w-5 h-5" />,
            document_verified: <FiCheck className="w-5 h-5" />
        }
        return iconMap[type]
    }

    const getNotificationColor = (type: NotificationType) => {
        const baseColors: Record<NotificationType, string> = {
            gig_application: 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400',
            application_status: 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400',
            message_received: 'text-purple-600 bg-purple-100 dark:bg-purple-900/20 dark:text-purple-400',
            gig_reminder: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400',
            payment_received: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400',
            payment_sent: 'text-orange-600 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400',
            profile_view: 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-400',
            rating_received: 'text-amber-600 bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400',
            system_update: 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400',
            account_security: 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400',
            promotional: 'text-pink-600 bg-pink-100 dark:bg-pink-900/20 dark:text-pink-400',
            welcome: 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400',
            email_verification: 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400',
            login_alert: 'text-orange-600 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400',
            password_changed: 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400',
            profile_updated: 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-400',
            account_deactivated: 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400',
            gig_posted: 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400',
            gig_updated: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400',
            gig_expired: 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400',
            gig_deleted: 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400',
            admin_broadcast: 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400',
            admin_individual: 'text-purple-600 bg-purple-100 dark:bg-purple-900/20 dark:text-purple-400',
            system_maintenance: 'text-orange-600 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400',
            policy_update: 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400',
            feature_announcement: 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400',
            verification: 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400',
            document_verification: 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400',
            verification_status_changed: 'text-amber-600 bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400',
            document_uploaded: 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400',
            verification_completed: 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400',
            document_rejected: 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400',
            document_approved: 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400',
            verification_update: 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400',
            document_verified: 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400'
        }

        return baseColors[type]
    }

    // Enhanced notification content based on type and data
    const getEnhancedNotificationContent = (notification: Notification) => {
        const { type, data, message, title } = notification

        switch (type) {
            case 'message_received':
                return {
                    title: `💬 New Message${data?.senderName ? ` from ${data.senderName}` : ''}`,
                    message: data?.gigTitle
                        ? `About "${data.gigTitle}" - Click to view conversation`
                        : 'Click to view your messages',
                    preview: data?.messagePreview || 'New message received'
                }

            case 'gig_application':
                return {
                    title: `📋 New Application${data?.senderName ? ` from ${data.senderName}` : ''}`,
                    message: data?.gigTitle
                        ? `Applied for "${data.gigTitle}" - Click to review application`
                        : 'New application received',
                    preview: 'Review their profile and decide if they\'re the right fit'
                }

            case 'application_status':
                const status = data?.status || 'updated'
                return {
                    title: `✅ Application ${status.charAt(0).toUpperCase() + status.slice(1)}`,
                    message: data?.gigTitle
                        ? `Your application for "${data.gigTitle}" was ${status}`
                        : `Application ${status}`,
                    preview: status === 'accepted' ? 'Congratulations! Check your messages for next steps' : 'Better luck next time!'
                }

            case 'payment_received':
                return {
                    title: `💰 Payment Received - $${data?.amount || '0'}`,
                    message: data?.gigTitle
                        ? `Payment for "${data.gigTitle}" - Click to view details`
                        : 'Payment received',
                    preview: 'Funds are being processed and will be available soon'
                }

            case 'payment_sent':
                return {
                    title: `💸 Payment Sent - $${data?.amount || '0'}`,
                    message: data?.gigTitle
                        ? `Payment for "${data.gigTitle}" - Click to view details`
                        : 'Payment sent',
                    preview: 'Payment has been processed successfully'
                }

            case 'rating_received':
                const rating = data?.rating || 0
                const stars = '⭐'.repeat(Math.floor(rating))
                return {
                    title: `⭐ New Rating - ${stars} (${rating}/5)`,
                    message: data?.gigTitle
                        ? `Rating for "${data.gigTitle}" - Click to view details`
                        : 'New rating received',
                    preview: 'Your work has been rated!'
                }

            case 'login_alert':
                return {
                    title: '🚨 New Login Alert',
                    message: 'Someone signed into your account',
                    preview: data?.location
                        ? `Location: ${data.location} • ${data?.deviceInfo || 'Unknown device'}`
                        : 'Check if this was you'
                }

            case 'admin_broadcast':
                return {
                    title: `📢 ${title}`,
                    message: message,
                    preview: data?.sentBy ? `From: ${data.sentBy}` : 'Important announcement from admin'
                }

            case 'admin_individual':
                return {
                    title: `💌 ${title}`,
                    message: message,
                    preview: data?.sentBy ? `Personal message from: ${data.sentBy}` : 'Personal message from admin'
                }

            case 'system_maintenance':
                return {
                    title: `🔧 ${title}`,
                    message: message,
                    preview: 'System maintenance notification - please check details'
                }

            case 'policy_update':
                return {
                    title: `📄 ${title}`,
                    message: message,
                    preview: 'Important policy update - please review'
                }

            case 'feature_announcement':
                return {
                    title: `🎉 ${title}`,
                    message: message,
                    preview: 'New feature available - explore now!'
                }

            case 'verification':
            case 'document_verification':
            case 'verification_status_changed':
            case 'verification_update':
                return {
                    title: `📄 ${title}`,
                    message: message || 'Document verification status updated',
                    preview: 'Check your verification dashboard for details'
                }

            case 'document_verified':
                return {
                    title: `✅ ${title || 'Document Verified'}`,
                    message: message || 'Your document has been successfully verified',
                    preview: data?.documentType ? `${data.documentType} verification completed` : 'Document verification completed'
                }

            case 'document_rejected':
                return {
                    title: `❌ ${title || 'Document Rejected'}`,
                    message: message || 'Your document verification was rejected',
                    preview: data?.rejectionReason || 'Check details for resubmission instructions'
                }

            case 'document_uploaded':
                return {
                    title: `📤 Document Uploaded`,
                    message: message || 'Your document has been submitted for verification',
                    preview: 'Document is being processed - you will be notified once reviewed'
                }

            case 'verification_completed':
                return {
                    title: `✅ Verification Complete`,
                    message: message || 'Your identity verification has been completed',
                    preview: 'Congratulations! You now have full access to all features'
                }

            case 'document_approved':
                return {
                    title: `✅ Document Approved`,
                    message: message || 'Your document has been approved',
                    preview: 'Great! Your document verification was successful'
                }

            default:
                return {
                    title: title,
                    message: message,
                    preview: null
                }
        }
    }

    // Handle notification click for navigation
    const handleNotificationClick = (notification: Notification) => {
        // Mark as read first
        if (!notification.read) {
            handleMarkAsRead(notification._id)
        }

        // Smart navigation based on notification type
        // Prioritize smart navigation over actionUrl for application notifications
        switch (notification.type) {
            case 'message_received':
                if (notification.data?.conversationId) {
                    navigate(`/messages/${notification.data.conversationId}`)
                } else {
                    navigate('/messages')
                }
                break

            case 'gig_application':
                if (notification.data?.applicationId) {
                    navigate(`/dashboard/applications/${notification.data.applicationId}`)
                } else {
                    navigate('/dashboard/applications')
                }
                break

            case 'application_status':
                if (notification.data?.applicationId) {
                    navigate(`/dashboard/applications/${notification.data.applicationId}`)
                } else {
                    navigate('/dashboard/applications')
                }
                break

            case 'payment_received':
            case 'payment_sent':
                navigate('/dashboard/payments')
                break

            case 'rating_received':
                navigate('/dashboard/reviews')
                break

            case 'gig_reminder':
            case 'gig_posted':
            case 'gig_updated':
            case 'gig_expired':
                if (notification.data?.gigId) {
                    navigate(`/gigs/${notification.data.gigId}`)
                } else {
                    navigate('/dashboard/my-gigs')
                }
                break

            case 'profile_view':
                navigate('/dashboard/profile')
                break

            case 'account_security':
            case 'login_alert':
            case 'password_changed':
                navigate('/dashboard/settings?tab=account')
                break

            case 'verification':
            case 'document_verification':
            case 'verification_status_changed':
            case 'document_uploaded':
            case 'verification_completed':
            case 'document_rejected':
            case 'document_approved':
            case 'verification_update':
            case 'document_verified':
                navigate('/dashboard/verification')
                break

            default:
                // For other types, stay on notifications page
                break
        }

        // Fallback to actionUrl for non-application notifications that don't have specific handling
        const handledTypes = [
            'gig_application',
            'application_status',
            'account_security',
            'login_alert',
            'password_changed',
            'verification',
            'document_verification',
            'verification_status_changed',
            'document_uploaded',
            'verification_completed',
            'document_rejected',
            'document_approved',
            'verification_update',
            'document_verified'
        ]

        if (notification.actionUrl && !handledTypes.includes(notification.type)) {
            if (notification.actionUrl.startsWith('/')) {
                navigate(notification.actionUrl)
            } else {
                window.open(notification.actionUrl, '_blank')
            }
            return
        }
    }

    const filteredNotifications = notifications.filter(notification => {
        const typeMatch = filterType === 'all' || notification.type === filterType
        const readMatch = filterRead === 'all' ||
            (filterRead === 'read' && notification.read) ||
            (filterRead === 'unread' && !notification.read)

        return typeMatch && readMatch
    })

    const handleRefresh = () => {
        dispatch(fetchNotifications({ page: 1, limit: 20 }))
        showSuccess('Notifications refreshed')
    }

    const handleMarkAsRead = async (notificationId: string) => {
        dispatch(markNotificationAsRead(notificationId))
        dispatch(markAsRead(notificationId))
    }

    const handleDeleteNotification = async (notificationId: string) => {
        dispatch(deleteNotification(notificationId))
    }

    const handleMarkAllAsRead = async () => {
        dispatch(markAllNotificationsAsRead())
        dispatch(markAllAsRead())
        showSuccess('All notifications marked as read')
    }

    const handleDeleteAll = async () => {
        showConfirmDialog(
            async () => {
                dispatch(deleteAllNotifications())
                showSuccess('All notifications deleted')
            },
            {
                title: 'Delete All Notifications',
                message: 'Are you sure you want to delete all notifications? This action cannot be undone.',
                confirmText: 'Delete All',
                cancelText: 'Cancel',
                type: 'danger'
            }
        )
    }

    const handleBulkAction = async (action: 'read' | 'delete') => {
        if (selectedNotifications.length === 0) return

        if (action === 'read') {
            selectedNotifications.forEach(id => {
                dispatch(markNotificationAsRead(id))
                dispatch(markAsRead(id))
            })
            showSuccess(`${selectedNotifications.length} notifications marked as read`)
            setSelectedNotifications([])
            setBulkMode(false)
        } else if (action === 'delete') {
            const count = selectedNotifications.length
            const notificationText = count === 1 ? 'notification' : 'notifications'

            showConfirmDialog(
                async () => {
                    selectedNotifications.forEach(id => {
                        dispatch(deleteNotification(id))
                    })
                    showSuccess(`${count} ${notificationText} deleted`)
                    setSelectedNotifications([])
                    setBulkMode(false)
                },
                {
                    title: `Delete Selected Notifications`,
                    message: `Are you sure you want to delete ${count} selected ${notificationText}? This action cannot be undone.`,
                    confirmText: `Delete ${count}`,
                    cancelText: 'Cancel',
                    type: 'danger'
                }
            )
        }
    }

    const toggleNotificationSelection = (notificationId: string) => {
        setSelectedNotifications(prev =>
            prev.includes(notificationId)
                ? prev.filter(id => id !== notificationId)
                : [...prev, notificationId]
        )
    }

    const loadMore = () => {
        if (!isLoading && hasMore) {
            dispatch(fetchNotifications({ page: currentPage + 1, limit: 20 }))
        }
    }

    return (
        <>
            <Helmet>
                <title>Notifications - SideGO</title>
                <meta name="description" content="View and manage your notifications" />
            </Helmet>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <FiBell className="w-8 h-8 text-blue-600" />
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                                Notifications
                            </h1>
                            {unreadCount > 0 && (
                                <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                                    You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowPreferences(true)}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                            title="Settings"
                        >
                            <FiSettings className="w-5 h-5" />
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                            title="Refresh"
                        >
                            <FiRefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm"
                        >
                            <FiFilter className="w-4 h-4" />
                            <span className="hidden sm:inline">Filters</span>
                            <FiChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Filters */}
                {showFilters && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs sm:text-sm md:text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Notification Type
                                </label>
                                <select
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value as NotificationType | 'all')}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {notificationTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs sm:text-sm md:text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Read Status
                                </label>
                                <select
                                    value={filterRead}
                                    onChange={(e) => setFilterRead(e.target.value as 'all' | 'read' | 'unread')}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="all">All</option>
                                    <option value="unread">Unread Only</option>
                                    <option value="read">Read Only</option>
                                </select>
                            </div>

                            <div className="flex items-end">
                                <button
                                    onClick={() => {
                                        setFilterType('all')
                                        setFilterRead('all')
                                        showSuccess('Filters cleared')
                                    }}
                                    className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions Bar */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setBulkMode(!bulkMode)}
                                className={`px-4 py-2 rounded-lg transition-colors text-sm ${bulkMode
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                Bulk Select
                            </button>

                            {bulkMode && selectedNotifications.length > 0 && (
                                <div className="flex items-center gap-2 ml-4">
                                    <span className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400 hidden sm:inline">
                                        {selectedNotifications.length} selected
                                    </span>
                                    <span className="text-xs text-gray-600 dark:text-gray-400 sm:hidden">
                                        {selectedNotifications.length}
                                    </span>
                                    <button
                                        onClick={() => handleBulkAction('read')}
                                        className="flex items-center gap-1 px-2 sm:px-3 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                                    >
                                        <FiEye className="w-4 h-4" />
                                        <span className="hidden sm:inline">Mark Read</span>
                                    </button>
                                    <button
                                        onClick={() => handleBulkAction('delete')}
                                        className="flex items-center gap-1 px-2 sm:px-3 py-1 text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                                    >
                                        <FiTrash2 className="w-4 h-4" />
                                        <span className="hidden sm:inline">Delete</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={handleMarkAllAsRead}
                                    className="flex items-center gap-2 px-3 sm:px-4 py-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-sm"
                                >
                                    <FiCheck className="w-4 h-4" />
                                    <span className="hidden sm:inline">Mark All Read</span>
                                    <span className="sm:hidden">Mark All</span>
                                </button>
                            )}

                            {notifications.length > 0 && (
                                <button
                                    onClick={handleDeleteAll}
                                    className="flex items-center gap-2 px-3 sm:px-4 py-2 text-red-600 hover:text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm"
                                >
                                    <FiTrash2 className="w-4 h-4" />
                                    <span className="hidden sm:inline">Delete All</span>
                                    <span className="sm:hidden">Delete All</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Notifications List */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    {isLoading && filteredNotifications.length === 0 ? (
                        <div className="flex items-center justify-center p-12">
                            <LoadingSpinner />
                        </div>
                    ) : filteredNotifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-12 text-gray-500 dark:text-gray-400">
                            <FiBell className="w-16 h-16 mb-4 text-gray-300 dark:text-gray-600" />
                            <h3 className="text-xs sm:text-sm md:text-base lg:text-lg font-medium mb-2">No notifications found</h3>
                            <p className="text-xs sm:text-sm md:text-base text-center">
                                {filterType !== 'all' || filterRead !== 'all'
                                    ? 'No notifications match your current filters.'
                                    : 'You\'ll see updates about your gigs and applications here.'
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                            {filteredNotifications.map((notification) => {
                                const enhancedContent = getEnhancedNotificationContent(notification)
                                const isClickable = notification.actionUrl || ['message_received', 'gig_application', 'application_status', 'payment_received', 'payment_sent', 'rating_received', 'gig_reminder', 'login_alert'].includes(notification.type)

                                return (
                                    <div
                                        key={notification._id}
                                        className={`relative p-4 sm:p-6 transition-all duration-200 ${!notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                                            } ${isClickable
                                                ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer hover:shadow-sm'
                                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                            }`}
                                        onClick={isClickable ? () => handleNotificationClick(notification) : undefined}
                                    >
                                        <div className="flex items-start gap-3 sm:gap-4">
                                            {/* Bulk Select Checkbox */}
                                            {bulkMode && (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedNotifications.includes(notification._id)}
                                                    onChange={(e) => {
                                                        e.stopPropagation()
                                                        toggleNotificationSelection(notification._id)
                                                    }}
                                                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                            )}

                                            {/* Icon */}
                                            <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center ${getNotificationColor(notification.type)}`}>
                                                {getNotificationIcon(notification.type)}
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                                                            <h3 className={`text-xs sm:text-sm md:text-base lg:text-lg font-semibold break-words ${!notification.read ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                                                                {enhancedContent.title}
                                                            </h3>
                                                            <div className="flex items-center gap-2">
                                                                {notification.priority === 'high' && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs sm:text-xs md:text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 flex-shrink-0">
                                                                        High Priority
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <p className={`text-xs sm:text-sm md:text-base mb-2 break-words ${!notification.read ? 'text-gray-700 dark:text-gray-200' : 'text-gray-600 dark:text-gray-400'}`}>
                                                            {enhancedContent.message}
                                                        </p>

                                                        {enhancedContent.preview && (
                                                            <p className="text-xs sm:text-sm md:text-base text-gray-500 dark:text-gray-500 mb-3 italic break-words">
                                                                {enhancedContent.preview}
                                                            </p>
                                                        )}

                                                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm md:text-base text-gray-500 dark:text-gray-500">
                                                            <span className="flex items-center gap-1 flex-shrink-0">
                                                                <FiBell className="w-3 h-3" />
                                                                {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                                                            </span>
                                                            <span className="capitalize hidden sm:inline text-xs sm:text-sm md:text-base">
                                                                {notification.type.replace('_', ' ')}
                                                            </span>
                                                            {notification.read && notification.readAt && (
                                                                <span className="flex items-center gap-1 text-green-600 dark:text-green-400 flex-shrink-0 text-xs sm:text-sm md:text-base">
                                                                    <FiEye className="w-3 h-3" />
                                                                    Read
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                                        {!notification.read && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleMarkAsRead(notification._id)
                                                                }}
                                                                className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                                                title="Mark as read"
                                                            >
                                                                <FiEye className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleDeleteNotification(notification._id)
                                                            }}
                                                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                            title="Delete notification"
                                                        >
                                                            <FiTrash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Unread indicator */}
                                            {!notification.read && (
                                                <div className="absolute right-4 top-4 w-3 h-3 bg-blue-500 rounded-full"></div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}

                            {/* Load More */}
                            {hasMore && (
                                <div className="p-6 text-center border-t border-gray-200 dark:border-gray-700">
                                    <button
                                        onClick={loadMore}
                                        disabled={isLoading}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? 'Loading...' : 'Load More Notifications'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Pagination Info */}
                {totalPages > 1 && (
                    <div className="mt-6 text-center text-xs sm:text-sm md:text-base text-gray-500 dark:text-gray-400">
                        Showing page {currentPage} of {totalPages}
                    </div>
                )}

                {/* Notification Preferences Modal */}
                {showPreferences && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-1 sm:p-2 z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col mt-2 sm:mt-0">
                            <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-sm sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white">
                                        Notification Preferences
                                    </h2>
                                    <button
                                        onClick={() => setShowPreferences(false)}
                                        className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center"
                                    >
                                        <FiX className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <NotificationPreferences onClose={() => setShowPreferences(false)} />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={dialogState.isOpen}
                onClose={closeDialog}
                onConfirm={handleConfirm}
                title={dialogState.title}
                message={dialogState.message}
                confirmText={dialogState.confirmText}
                cancelText={dialogState.cancelText}
                type={dialogState.type}
                loading={dialogState.loading}
            />
        </>
    )
}

export default NotificationsPage